import { HKPImage } from "../../media/HKPImage";
import React, { useEffect, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import MyCollectionLayout from "./MyCollectionLayout";
import { getCollectorMe, isCollectorSessionError, listCollectorItems } from "../../services/collectorApi";
import { collectorHelpWhatsAppUrl, collectorLoginPath, getCollectorToken } from "../../helpers/collectorSession";
import { collectorRecordKey } from "../../helpers/collectorIds";
import {
  collectionTitle,
  firstDemoItem,
  latestPieceWithoutStory,
  partitionCollectionItems,
  sawThisLabel,
  storyFirstLine,
  type CollectionRoom,
} from "../../helpers/collectorRooms";

type Item = {
  id?: string;
  _id?: string;
  collectorItemId: string;
  title: string;
  itemType: string;
  status: string;
  coverPhotoUrl?: string;
  story?: string;
  isSampleItem?: boolean;
  lastScannedAt?: string;
  createdAt?: string;
  productPreview?: {
    coverPhotoUrl?: string;
  };
};

type Profile = {
  displayName?: string;
};

const ONBOARDING_HINTS = [
  "Add a piece you own — a photo, a title, and a short story.",
  "Tap a physical HKP tag, then verify your phone. That receipt lands in Scanned.",
  "On a piece you keep, open Share & print for a QR card that stays with the cloth.",
  "Only that one piece is shown on a public QR. Your collection stays private.",
];

const HINTS_KEY = "mc_onboarding_hints_done";

type Props = {
  room?: CollectionRoom;
};

const MyCollectionDashboard: React.FC<Props> = ({ room }) => {
  const history = useHistory();
  const location = useLocation();
  const activeRoom: CollectionRoom =
    room || (location.pathname.startsWith("/my-collection/scanned") ? "scanned" : "pieces");
  const hasToken = Boolean(getCollectorToken());
  const [loading, setLoading] = useState(hasToken);
  const [items, setItems] = useState<Item[]>([]);
  const [view, setView] = useState<"grid" | "list">(
    () => (localStorage.getItem("mc_view_mode") as "grid" | "list") || "grid"
  );
  const [profile, setProfile] = useState<Profile>({});
  const [sessionInvalid, setSessionInvalid] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showHints, setShowHints] = useState(() => localStorage.getItem(HINTS_KEY) !== "1");
  const [wide, setWide] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setWide(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!hasToken) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [meRes, itemsRes] = await Promise.all([getCollectorMe(), listCollectorItems()]);
        if (cancelled) return;
        setProfile(meRes.data?.data || {});
        setItems(itemsRes.data?.data?.items || []);
      } catch (err) {
        if (cancelled) return;
        // Only a dead session sends the person to sign in; a network or server error shows a retry.
        if (isCollectorSessionError(err)) setSessionInvalid(true);
        else setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasToken]);

  if (sessionInvalid) {
    // A returning person whose session could not be renewed signs in and comes straight back.
    // Checked first: a refused session is cleared, so there is no token by now either.
    return <Redirect to={collectorLoginPath(location.pathname + location.search)} />;
  }
  if (!hasToken) {
    return <Redirect to="/collectors" />;
  }

  const { scanned, pieces, samples } = partitionCollectionItems(items);
  const storyWanted = latestPieceWithoutStory(pieces.filter((p) => p.itemType === "EXTERNAL"));
  const helpUrl = collectorHelpWhatsAppUrl();
  const roomItems = activeRoom === "scanned" ? scanned : pieces;
  const demo = firstDemoItem(samples);
  const isScanned = activeRoom === "scanned";
  const firstRowSize = view === "grid" && !isScanned && wide ? 2 : 1;
  const headItems = roomItems.slice(0, firstRowSize);
  const restItems = roomItems.slice(firstRowSize);

  const toggleView = (mode: "grid" | "list") => {
    setView(mode);
    localStorage.setItem("mc_view_mode", mode);
  };

  const hideHints = () => {
    localStorage.setItem(HINTS_KEY, "1");
    setShowHints(false);
  };

  const openItem = (item: Item) => {
    history.push(`/my-collection/item/${collectorRecordKey(item)}`);
  };

  const demoLink = demo ? (
    <button
      type="button"
      className="btn btn-link btn-sm p-0"
      onClick={() => openItem(demo)}
    >
      Try a demo piece
    </button>
  ) : null;

  const gettingStarted = showHints ? (
    <details className="mc-card mc-onboarding mb-3">
      <summary className="h6 mb-0">Getting started</summary>
      <ol className="mb-2 mt-2 ps-3 small">
        {ONBOARDING_HINTS.map((hint) => (
          <li key={hint} className="mb-1">{hint}</li>
        ))}
      </ol>
      <button type="button" className="mc-btn-ghost" onClick={hideHints}>
        Hide this
      </button>
    </details>
  ) : null;

  const renderCard = (item: Item) => {
    const cover = item.coverPhotoUrl || item.productPreview?.coverPhotoUrl;
    if (isScanned) {
      return (
        <button
          type="button"
          key={item.collectorItemId}
          className="mc-scan-card"
          onClick={() => openItem(item)}
        >
          {cover ? (
            <HKPImage allowMaster={false} preset="PRODUCT_CARD" legacyUrl={cover} alt="" />
          ) : (
            <span className="mc-scan-card-fallback" aria-hidden="true" />
          )}
          <span className="mc-scan-card-body">
            <span className="fw-semibold d-block">{item.title}</span>
            <span className="mc-scan-stamp">{sawThisLabel(item.lastScannedAt || item.createdAt)}</span>
            <span className="mc-scan-mark">HKP</span>
          </span>
        </button>
      );
    }
    const line = storyFirstLine(item.story);
    return (
      <button
        type="button"
        key={item.collectorItemId}
        className={view === "grid" ? "mc-jewel-card" : "mc-jewel-card mc-jewel-card-row"}
        onClick={() => openItem(item)}
      >
        {cover ? (
          <HKPImage allowMaster={false} preset="PRODUCT_CARD" legacyUrl={cover} alt="" />
        ) : (
          <span className="mc-jewel-card-fallback" aria-hidden="true" />
        )}
        <span className="mc-jewel-card-body">
          <span className="fw-semibold d-block">{item.title}</span>
          {line ? <span className="mc-jewel-story">{line}</span> : null}
        </span>
      </button>
    );
  };

  if (loading) {
    return (
      <MyCollectionLayout>
        <div className="text-center py-5"><Spinner /></div>
      </MyCollectionLayout>
    );
  }

  if (loadFailed) {
    return (
      <MyCollectionLayout>
        <Alert color="warning" className="mt-3">
          Could not load your collection. Check your internet and try again.
        </Alert>
        <Button className="mc-btn-primary" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </MyCollectionLayout>
    );
  }

  return (
    <MyCollectionLayout>
      {isScanned ? (
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h1 className="h3 mb-1">{collectionTitle(profile.displayName)}</h1>
            <p className="text-muted small mb-0">Receipts of tags you tapped. Not a claim of ownership.</p>
          </div>
          <Button className="mc-btn-secondary" onClick={() => history.push("/my-collection/how-to-scan")}>
            How to scan
          </Button>
        </div>
      ) : (
        <div className="mc-home">
          <h1 className="mc-qa-title">{welcomeTitle(profile.displayName, roomItems.length)}</h1>
          {roomItems.length > 0 && (
            <p className="mc-qa-lead">
              You keep {roomItems.length} {roomItems.length === 1 ? "piece" : "pieces"} here.
            </p>
          )}
          <section className="mc-home-add">
            <Button className="mc-btn-primary mc-qa-wide" onClick={() => history.push("/my-collection/add")}>
              + Add a piece
            </Button>
            <p>One photo is enough. Add the rest later.</p>
          </section>
          {storyWanted && (
            <section className="mc-home-last" aria-label="Last added">
              <h2>Last added</h2>
              <button type="button" className="mc-home-last-card" onClick={() => history.push(`/my-collection/item/${collectorRecordKey(storyWanted)}?edit=1`)}>
                {storyWanted.coverPhotoUrl ? (
                  <HKPImage allowMaster={false} preset="THUMBNAIL" legacyUrl={storyWanted.coverPhotoUrl} alt="" />
                ) : (
                  <span className="mc-jewel-card-fallback" aria-hidden="true" />
                )}
                <span className="mc-qa-next-text">
                  <strong>{storyWanted.title}</strong>
                  <span>No story yet</span>
                  <span className="mc-home-last-cta">Add its story</span>
                </span>
              </button>
            </section>
          )}
          {roomItems.length > 0 && <h2 className="mc-home-section">Your pieces</h2>}
        </div>
      )}

      {!isScanned && roomItems.length > 0 && (
        <div className="mb-3" role="group" aria-label="View mode">
          <button
            type="button"
            aria-pressed={view === "grid"}
            className={`mc-toggle me-2 ${view === "grid" ? "is-active" : ""}`}
            onClick={() => toggleView("grid")}
          >
            Grid
          </button>
          <button
            type="button"
            aria-pressed={view === "list"}
            className={`mc-toggle ${view === "list" ? "is-active" : ""}`}
            onClick={() => toggleView("list")}
          >
            List
          </button>
        </div>
      )}

      {roomItems.length === 0 ? (
        <div className="mc-empty-room text-center py-5">
          {isScanned ? (
            <>
              <p>No scans yet. Tap a physical tag, then verify your phone on the certificate.</p>
              <Button className="mc-btn-primary mc-btn-block" onClick={() => history.push("/my-collection/how-to-scan")}>
                How to scan
              </Button>
            </>
          ) : (
            <>
              <p>Nothing here yet. Your first piece takes one photo.</p>
            </>
          )}
          {demoLink && <div className="mt-3">{demoLink}</div>}
          {!isScanned && gettingStarted}
        </div>
      ) : (
        <>
          <div className={isScanned ? "mc-scan-grid" : view === "grid" ? "mc-jewel-grid" : "mc-jewel-list"}>
            {headItems.map(renderCard)}
          </div>
          {!isScanned && gettingStarted}
          {restItems.length > 0 && (
            <div className={isScanned ? "mc-scan-grid" : view === "grid" ? "mc-jewel-grid" : "mc-jewel-list"}>
              {restItems.map(renderCard)}
            </div>
          )}
        </>
      )}
      {helpUrl && (
        <a className="mc-home-help" href={helpUrl} target="_blank" rel="noreferrer">
          Need help? WhatsApp us
        </a>
      )}
    </MyCollectionLayout>
  );
};

const welcomeTitle = (displayName: string | undefined, count: number): string => {
  const name = String(displayName || "").trim();
  if (count === 0) return name ? `Welcome, ${name}` : "My Collection";
  return name ? `Welcome back, ${name}` : "Welcome back";
};

export default MyCollectionDashboard;

